import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSingleColumn,
  NamedRedirect,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

const TABS = {
  ITEMS: 'items',
  REVIEWS: 'reviews',
  ABOUT: 'about',
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;
  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

const CoverPhotoSection = ({ coverPhotoUrl, user, displayName, showEditLink }) => {
  return (
    <div className={css.coverSection}>
      <div className={css.coverPhoto}>
        {coverPhotoUrl ? (
          <img src={coverPhotoUrl} alt="Cover" className={css.coverImage} />
        ) : (
          <div className={css.coverPlaceholder} />
        )}
      </div>
      <div className={css.profileHeader}>
        <div className={css.profileHeaderInner}>
          <AvatarLarge className={css.profileAvatar} user={user} disableProfileLink />
          <div className={css.profileInfo}>
            <h1 className={css.shopName}>
              {displayName || user?.attributes?.profile?.displayName || ''}
            </h1>
            {showEditLink ? (
              <NamedLink className={css.editProfileLink} name="ProfileSettingsPage">
                <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
              </NamedLink>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const CARD_RENDER_SIZES = [
  '(max-width: 549px) 50vw',
  '(max-width: 767px) 50vw',
  '(max-width: 1023px) 33vw',
  '(max-width: 1200px) 25vw',
  '20vw',
].join(', ');

const FEATURED_CARD_RENDER_SIZES = [
  '(max-width: 549px) 70vw',
  '(max-width: 767px) 45vw',
  '(max-width: 1023px) 33vw',
  '25vw',
].join(', ');

const ItemsTab = ({ listings }) => {
  const featuredListings = listings.filter(l => l?.attributes?.publicData?.isFeatured === true);
  const allListings = listings;

  return (
    <div className={css.tabContent}>
      {featuredListings.length > 0 ? (
        <div className={css.featuredSection}>
          <H4 as="h2" className={css.sectionTitle}>
            <FormattedMessage
              id="ProfilePage.featuredItemsTitle"
              values={{ count: featuredListings.length }}
            />
          </H4>
          <div className={css.featuredScroll}>
            {featuredListings.map(l => (
              <div className={css.featuredItem} key={l.id.uuid}>
                <ListingCard
                  listing={l}
                  showAuthorInfo={false}
                  renderSizes={FEATURED_CARD_RENDER_SIZES}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {allListings.length > 0 ? (
        <div className={css.allItemsSection}>
          <H4 as="h2" className={css.sectionTitle}>
            <FormattedMessage
              id="ProfilePage.allItemsTitle"
              values={{ count: allListings.length }}
            />
          </H4>
          <ul className={css.listingsGrid}>
            {allListings.map(l => (
              <li className={css.listingItem} key={l.id.uuid}>
                <ListingCard
                  listing={l}
                  showAuthorInfo={false}
                  renderSizes={CARD_RENDER_SIZES}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className={css.noContent}>
          <FormattedMessage id="ProfilePage.noListings" />
        </p>
      )}
    </div>
  );
};

const ReviewsTab = ({ reviews, queryReviewsError, userTypeRoles, intl }) => {
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;
  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;

  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.reviewsTabTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.reviewsTabTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: !isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];

  const reviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div className={css.tabContent}>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      {reviewTabs.length > 1 ? (
        <ButtonTabNavHorizontal
          className={css.reviewsSubNav}
          tabs={reviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />
      ) : null}
      {isReviewTypeProviderSelected ? (
        <Reviews reviews={reviewsOfProvider} />
      ) : (
        <Reviews reviews={reviewsOfCustomer} />
      )}
      {reviews.length === 0 ? (
        <p className={css.noContent}>
          <FormattedMessage id="ProfilePage.noReviews" />
        </p>
      ) : null}
    </div>
  );
};

const AboutTab = ({ bio, displayName, publicData, metadata, userFieldConfig, intl }) => {
  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  return (
    <div className={css.tabContent}>
      {hasBio ? (
        <div className={css.aboutBio}>
          <H4 as="h2" className={css.sectionTitle}>
            <FormattedMessage id="ProfilePage.aboutSeller" />
          </H4>
          <p className={css.bio}>{bioWithLinks}</p>
        </div>
      ) : null}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      {!hasBio && !displayName ? (
        <p className={css.noContent}>
          <FormattedMessage id="ProfilePage.noAboutInfo" />
        </p>
      ) : null}
    </div>
  );
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.ITEMS);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    listings = [],
    reviews = [],
    queryListingsError,
    queryReviewsError,
  } = props;

  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  const searchParams = props?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;
  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const coverPhotoUrl = publicData?.coverPhotoUrl || null;

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    return null;
  }

  if (userShowError || queryListingsError) {
    return (
      <Page scrollingDisabled={scrollingDisabled} title={schemaTitle}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <p className={css.error}>
            <FormattedMessage id="ProfilePage.loadingDataFailed" />
          </p>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const tabs = [
    {
      text: (
        <span className={css.tabText}>
          <FormattedMessage id="ProfilePage.tabItems" />
          <span className={css.tabCount}>{listings.length}</span>
        </span>
      ),
      selected: activeTab === TABS.ITEMS,
      onClick: () => setActiveTab(TABS.ITEMS),
    },
    {
      text: (
        <span className={css.tabText}>
          <FormattedMessage id="ProfilePage.tabReviews" />
          <span className={css.tabCount}>{reviews.length}</span>
        </span>
      ),
      selected: activeTab === TABS.REVIEWS,
      onClick: () => setActiveTab(TABS.REVIEWS),
    },
    {
      text: (
        <span className={css.tabText}>
          <FormattedMessage id="ProfilePage.tabAbout" />
        </span>
      ),
      selected: activeTab === TABS.ABOUT,
      onClick: () => setActiveTab(TABS.ABOUT),
    },
  ];

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <CoverPhotoSection
          coverPhotoUrl={coverPhotoUrl}
          user={profileUser}
          displayName={displayName}
          showEditLink={mounted && isCurrentUser}
        />

        <div className={css.shopContent}>
          <ButtonTabNavHorizontal
            className={css.shopTabs}
            tabs={tabs}
            ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.shopNav' })}
          />

          {activeTab === TABS.ITEMS ? <ItemsTab listings={listings} /> : null}

          {activeTab === TABS.REVIEWS ? (
            <ReviewsTab
              reviews={reviews}
              queryReviewsError={queryReviewsError}
              userTypeRoles={userTypeRoles}
              intl={intl}
            />
          ) : null}

          {activeTab === TABS.ABOUT ? (
            <AboutTab
              bio={bio}
              displayName={displayName}
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={userFields}
              intl={intl}
            />
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
